import type { paramsType } from '~~/modules/api/rurality/v3/search/cottages/paramsSchema'

export interface CottageListFilters {
  f?: string
  l?: string
  minPrice?: number
  maxPrice?: number
  dateIn?: string
  dateOut?: string
  c?: number
  ec?: number
  r?: string
  bo?: number
  sort?: string
  q?: string
}

export function useCottageListFilters() {
  const route = useRoute()

  /**
   * Parse URL query parameters into filter object
   */
  function parseUrlParams(): paramsType {
    const query = route.query
    return {
      l: (query.l as string) || undefined,
      f: (query.f as string) || undefined,
      min_price: query.min_price ? Number(query.min_price) : undefined,
      max_price: query.max_price ? Number(query.max_price) : undefined,
      d: (query.d as string) || undefined,
      c: query.c ? Number(query.c) : undefined,
      ec: query.ec ? Number(query.ec) : undefined,
      r: (query.r as string) || undefined,
      bo: query.bo ? Number(query.bo) : undefined,
      sort: (query.sort as string) || undefined,
      q: (query.q as string) || undefined,
      page: query.page ? Number(query.page) : 1,
    }
  }

  /**
   * Build URL query object from filters
   */
  function buildUrlQuery(
    filters: CottageListFilters,
    page?: number,
    currentPage?: number,
  ): Record<string, string> {
    const query: Record<string, string> = {}

    query.l = filters.l || 'espana'

    if (filters.f) query.f = filters.f
    if (filters.minPrice !== undefined)
      query.min_price = String(filters.minPrice)
    if (filters.maxPrice !== undefined)
      query.max_price = String(filters.maxPrice)
    if (filters.dateIn && filters.dateOut) {
      query.d = `${filters.dateIn},${filters.dateOut}`
    }
    if (filters.c !== undefined) query.c = String(filters.c)
    if (filters.ec !== undefined) query.ec = String(filters.ec)
    if (filters.r !== undefined) query.r = filters.r
    if (filters.bo !== undefined) query.bo = String(filters.bo)
    if (filters.sort) query.sort = filters.sort
    if (filters.q) query.q = filters.q

    const pageNumber = page || currentPage || 1
    if (pageNumber > 1) query.page = String(pageNumber)

    // Preserve viz parameter if it exists
    if (route.query.viz === 'map') {
      query.viz = 'map'
    }

    return query
  }

  /**
   * Navigate to results page with filters
   */
  function navigateWithFilters(
    filters: CottageListFilters,
    page?: number,
    currentPage?: number,
  ) {
    const query = buildUrlQuery(filters, page, currentPage)

    navigateTo(
      {
        path: routes().cottageList(),
        query,
      },
      { external: true },
    )
  }

  /**
   * Get current filters from URL
   */
  function getCurrentFilters(): CottageListFilters {
    const params = parseUrlParams()
    const dates = params.d ? params.d.split(',') : []
    return {
      l: params.l,
      f: params.f,
      minPrice: params.min_price,
      maxPrice: params.max_price,
      dateIn: dates[0] || undefined,
      dateOut: dates[1] || undefined,
      c: params.c,
      ec: params.ec,
      r: params.r,
      bo: params.bo,
      sort: params.sort,
      q: params.q,
    }
  }

  /**
   * Handle location change
   */
  function handleLocationChange(location: string) {
    const currentFilters = getCurrentFilters()
    navigateWithFilters(
      {
        ...currentFilters,
        l: location,
      },
      1,
    )
  }

  /**
   * Handle online booking filter toggle
   */
  function handleOnlineBookingFilter() {
    const currentFilters = getCurrentFilters()
    navigateWithFilters(
      {
        ...currentFilters,
        bo: 1,
      },
      1,
    )
  }

  /**
   * Handle exact capacity filter toggle
   */
  function handleExactCapacityFilter() {
    const currentFilters = getCurrentFilters()
    navigateWithFilters(
      {
        ...currentFilters,
        ec: 1,
      },
      1,
    )
  }

  /**
   * Remove a specific filter by ID
   */
  function handleRemoveFilter(filterId: string) {
    const currentFilters = getCurrentFilters()
    const filtersArray = currentFilters.f ? currentFilters.f.split(',') : []

    const updatedFilters = filtersArray.filter(f => f !== filterId)

    navigateWithFilters(
      {
        ...currentFilters,
        f: updatedFilters.length > 0 ? updatedFilters.join(',') : undefined,
      },
      1,
    )
  }

  /**
   * Update filters and navigate
   */
  function updateFilters(filters: CottageListFilters, page?: number) {
    navigateWithFilters(filters, page)
  }

  function hasFiltersApplied(): boolean {
    const currentFilters = getCurrentFilters()
    return (
      !!currentFilters.f ||
      !!currentFilters.minPrice ||
      !!currentFilters.maxPrice ||
      !!currentFilters.dateIn ||
      !!currentFilters.dateOut ||
      !!currentFilters.c ||
      !!currentFilters.ec ||
      !!currentFilters.r ||
      !!currentFilters.bo ||
      !!currentFilters.sort ||
      !!currentFilters.q
    )
  }

  return {
    parseUrlParams,
    buildUrlQuery,
    navigateWithFilters,
    getCurrentFilters,
    handleLocationChange,
    handleOnlineBookingFilter,
    handleExactCapacityFilter,
    handleRemoveFilter,
    updateFilters,
    hasFiltersApplied,
  }
}
